import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { X, Building, Loader, AlertCircle, CheckCircle, Upload, Trash2 } from 'lucide-react';
import { GET_DATOS_HOTEL, UPDATE_DATOS_HOTEL } from '../../graphql/configuracion';
import './ModalConfig.css';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4005/graphql').replace('/graphql', '');

function ModalDatosHotel({ onClose }) {
  const [formData, setFormData] = useState({});
  const [mensaje, setMensaje] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  // Query para obtener datos actuales
  const { data, loading, error } = useQuery(GET_DATOS_HOTEL);

  // Mutation para actualizar
  const [actualizarDatos, { loading: saving }] = useMutation(UPDATE_DATOS_HOTEL, {
    onCompleted: () => {
      setMensaje({ tipo: 'success', texto: 'Datos actualizados exitosamente' });
      setTimeout(() => {
        onClose();
      }, 1500);
    },
    onError: (error) => {
      setMensaje({ tipo: 'error', texto: error.message });
    },
  });

  // Cargar datos cuando estén disponibles
  useEffect(() => {
    if (data?.datosHotel) {
      setFormData(data.datosHotel);
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMensaje({ tipo: 'error', texto: 'La imagen no debe superar 2MB' });
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setMensaje({ tipo: 'error', texto: 'Solo se permiten imágenes JPG, PNG o WebP' });
      return;
    }

    setUploadingLogo(true);
    setMensaje(null);

    try {
      const formPayload = new FormData();
      formPayload.append('logo', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/upload-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formPayload,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al subir imagen');
      }

      const { url } = await res.json();
      setFormData(prev => ({ ...prev, logo_url: `${API_BASE}${url}` }));
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.message });
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Preparar input (excluir campos del sistema y campos no definidos en DatosHotelInput)
    const { id, created_at, updated_at, __typename, activo, propiedad_id,
      resolucion_dian, fecha_inicio_resolucion, fecha_fin_resolucion,
      prefijo_factura, numero_actual_factura, rango_inicial_factura,
      rango_final_factura, ambiente_dian, ...input } = formData;

    await actualizarDatos({
      variables: { input }
    });
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-config" onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Loader size={40} className="spinner" />
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Cargando datos...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-config" onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <AlertCircle size={40} color="#ef4444" />
            <p style={{ marginTop: '1rem', color: '#ef4444' }}>Error al cargar datos: {error.message}</p>
            <button onClick={onClose} className="btn-secondary" style={{ marginTop: '1rem' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-config modal-config--large">
        {/* Header */}
        <div className="modal-config__header">
          <div className="modal-config__header-icon">
            <Building size={24} />
          </div>
          <div>
            <h2 className="modal-config__title">Datos del Hotel</h2>
            <p className="modal-config__subtitle">Información legal y NIT</p>
          </div>
          <button onClick={onClose} className="modal-config__close">
            <X size={20} />
          </button>
        </div>

        {/* Mensaje de estado */}
        {mensaje && (
          <div className={`alert alert--${mensaje.tipo}`}>
            {mensaje.tipo === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{mensaje.texto}</span>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="modal-config__form">
          <div className="form-grid">
            {/* Sección 1: Información Legal */}
            <div className="form-section">
              <h3 className="form-section__title">Información Legal</h3>

              <div className="form-field">
                <label htmlFor="nombre_comercial">Nombre Comercial *</label>
                <input
                  type="text"
                  id="nombre_comercial"
                  name="nombre_comercial"
                  value={formData.nombre_comercial || ''}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-field">
                <label htmlFor="razon_social">Razón Social *</label>
                <input
                  type="text"
                  id="razon_social"
                  name="razon_social"
                  value={formData.razon_social || ''}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="nit">NIT *</label>
                  <input
                    type="text"
                    id="nit"
                    name="nit"
                    value={formData.nit || ''}
                    onChange={handleChange}
                    required
                    placeholder="900123456"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="digito_verificacion">DV</label>
                  <input
                    type="text"
                    id="digito_verificacion"
                    name="digito_verificacion"
                    value={formData.digito_verificacion || ''}
                    onChange={handleChange}
                    maxLength="1"
                    placeholder="7"
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="tipo_persona">Tipo de Persona *</label>
                <select
                  id="tipo_persona"
                  name="tipo_persona"
                  value={formData.tipo_persona || 'juridica'}
                  onChange={handleChange}
                  required
                >
                  <option value="juridica">Persona Jurídica</option>
                  <option value="natural">Persona Natural</option>
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="regimen_tributario">Régimen Tributario *</label>
                <select
                  id="regimen_tributario"
                  name="regimen_tributario"
                  value={formData.regimen_tributario || 'simplificado'}
                  onChange={handleChange}
                  required
                >
                  <option value="simplificado">Simplificado</option>
                  <option value="comun">Común</option>
                  <option value="especial">Especial</option>
                </select>
              </div>
            </div>

            {/* Sección 2: Ubicación y Contacto */}
            <div className="form-section">
              <h3 className="form-section__title">Ubicación y Contacto</h3>

              <div className="form-field">
                <label htmlFor="direccion">Dirección *</label>
                <input
                  type="text"
                  id="direccion"
                  name="direccion"
                  value={formData.direccion || ''}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="ciudad">Ciudad *</label>
                  <input
                    type="text"
                    id="ciudad"
                    name="ciudad"
                    value={formData.ciudad || ''}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="departamento">Departamento *</label>
                  <input
                    type="text"
                    id="departamento"
                    name="departamento"
                    value={formData.departamento || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="codigo_postal">Código Postal</label>
                  <input
                    type="text"
                    id="codigo_postal"
                    name="codigo_postal"
                    value={formData.codigo_postal || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="pais">País *</label>
                  <input
                    type="text"
                    id="pais"
                    name="pais"
                    value={formData.pais || 'Colombia'}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="telefono">Teléfono</label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono || ''}
                    onChange={handleChange}
                    placeholder="(+57) 601 234 5678"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="celular">Celular</label>
                  <input
                    type="tel"
                    id="celular"
                    name="celular"
                    value={formData.celular || ''}
                    onChange={handleChange}
                    placeholder="(+57) 300 123 4567"
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  required
                  placeholder="info@hotel.com"
                />
              </div>

              <div className="form-field">
                <label htmlFor="sitio_web">Sitio Web</label>
                <input
                  type="url"
                  id="sitio_web"
                  name="sitio_web"
                  value={formData.sitio_web || ''}
                  onChange={handleChange}
                  placeholder="https://www.hotel.com"
                />
              </div>
            </div>

            {/* Sección 3: Branding */}
            <div className="form-section">
              <h3 className="form-section__title">Branding</h3>

              <div className="form-field">
                <label>Logo del Hotel</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />

                {formData.logo_url ? (
                  <div className="logo-preview">
                    <img src={formData.logo_url} alt="Logo del hotel" className="logo-preview__img" />
                    <div className="logo-preview__actions">
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        Cambiar
                      </button>
                      <button
                        type="button"
                        className="btn-danger btn-sm"
                        onClick={handleRemoveLogo}
                        disabled={uploadingLogo}
                      >
                        <Trash2 size={14} />
                        Eliminar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="logo-upload-zone"
                    onClick={() => !uploadingLogo && fileInputRef.current?.click()}
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader size={24} className="spinner" />
                        <span>Subiendo imagen...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={28} />
                        <span>Seleccionar imagen</span>
                        <small>PNG, JPG o WebP. Max 2MB</small>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="color_primario">Color Primario</label>
                  <input
                    type="color"
                    id="color_primario"
                    name="color_primario"
                    value={formData.color_primario || '#1e40af'}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="color_secundario">Color Secundario</label>
                  <input
                    type="color"
                    id="color_secundario"
                    name="color_secundario"
                    value={formData.color_secundario || '#06b6d4'}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="eslogan">Eslogan</label>
                <input
                  type="text"
                  id="eslogan"
                  name="eslogan"
                  value={formData.eslogan || ''}
                  onChange={handleChange}
                  placeholder="Tu mejor experiencia de hospedaje"
                />
              </div>

              <div className="form-field">
                <label htmlFor="descripcion_empresa">Descripción</label>
                <textarea
                  id="descripcion_empresa"
                  name="descripcion_empresa"
                  value={formData.descripcion_empresa || ''}
                  onChange={handleChange}
                  placeholder="Descripción de la empresa"
                />
              </div>
            </div>

            {/* Sección 5: Configuración Regional */}
            <div className="form-section form-section--full">
              <h3 className="form-section__title">Configuración Regional</h3>

              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="moneda">Moneda *</label>
                  <select
                    id="moneda"
                    name="moneda"
                    value={formData.moneda || 'COP'}
                    onChange={handleChange}
                    required
                  >
                    <option value="COP">COP - Peso Colombiano</option>
                    <option value="USD">USD - Dólar</option>
                    <option value="EUR">EUR - Euro</option>
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="timezone">Zona Horaria *</label>
                  <select
                    id="timezone"
                    name="timezone"
                    value={formData.timezone || 'America/Bogota'}
                    onChange={handleChange}
                    required
                  >
                    <option value="America/Bogota">America/Bogota</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="Europe/Madrid">Europe/Madrid</option>
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="idioma">Idioma *</label>
                  <select
                    id="idioma"
                    name="idioma"
                    value={formData.idioma || 'es'}
                    onChange={handleChange}
                    required
                  >
                    <option value="es">Español</option>
                    <option value="en">English</option>
                    <option value="pt">Português</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer con botones */}
          <div className="modal-config__footer">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader size={16} className="spinner" />
                  <span>Guardando...</span>
                </>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModalDatosHotel;
